import Link from "next/link";
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { GoogleTagManager } from "@next/third-parties/google";
import type { AnalyticsContext } from "@/lib/analytics";
import {
  applyAnalyticsConsentDefaultDenied,
  applyAnalyticsConsentDenied,
  applyAnalyticsConsentGranted,
  clearAnalyticsCookies,
  readStoredCookieConsent,
  storeCookieConsent,
  subscribeToCookieConsent,
  subscribeToCookieConsentPrompt,
} from "@/lib/cookie-consent";
import {
  buildStorefrontThemeVariables,
  type StorefrontSiteCode,
} from "@/lib/storefront-site";

type CookieConsentManagerProps = {
  gtmId: string | null;
  analyticsContext: AnalyticsContext;
  siteCode: StorefrontSiteCode;
  /**
   * Suppresses the banner UI on the standalone payment / app-WebView checkout
   * / error routes (never interrupt a payment). GTM stays consent-gated on
   * those routes regardless — no consent, no GTM.
   */
  allowBanner: boolean;
};

// The consent choice lives in localStorage and changes via window events, so
// it is read through useSyncExternalStore: the server snapshot is null (no
// banner in SSR HTML) and the client re-reads storage whenever
// storeCookieConsent broadcasts a change.
function subscribeChoice(onStoreChange: () => void) {
  return subscribeToCookieConsent(() => onStoreChange());
}

function serverChoiceSnapshot() {
  return null;
}

// Standard mount detector without setState-in-effect: the store never
// changes, but server and client snapshots differ.
function subscribeNever() {
  return () => {};
}

/**
 * Owns the cookie-consent lifecycle: pushes the Consent Mode v2 denied
 * defaults before any Google script can run, renders the GTM container only
 * once analytics consent is granted, and shows the consent banner until the
 * visitor chooses (or when the footer "Cookie preferences" link re-opens it).
 */
export function CookieConsentManager({
  gtmId,
  analyticsContext,
  siteCode,
  allowBanner,
}: CookieConsentManagerProps) {
  const choice = useSyncExternalStore(
    subscribeChoice,
    readStoredCookieConsent,
    serverChoiceSnapshot,
  );
  const hydrated = useSyncExternalStore(
    subscribeNever,
    () => true,
    () => false,
  );
  const [promptOpen, setPromptOpen] = useState(false);

  useEffect(() => {
    // Consent defaults must be queued ahead of the GTM script — GTM replays
    // the dataLayer in order, so denied-by-default always lands first. A
    // previously-granted choice is re-asserted right after.
    applyAnalyticsConsentDefaultDenied();
    if (readStoredCookieConsent() === "granted") {
      applyAnalyticsConsentGranted();
    }

    return subscribeToCookieConsentPrompt(() => setPromptOpen(true));
  }, []);

  const handleAccept = useCallback(() => {
    applyAnalyticsConsentGranted();
    storeCookieConsent("granted");
    setPromptOpen(false);
  }, []);

  const handleDecline = useCallback(() => {
    // Flip the live consent state too: on the decline-after-grant path the
    // GTM script is already running and only a consent update stops it
    // writing further cookies this session.
    applyAnalyticsConsentDenied();
    storeCookieConsent("denied");
    clearAnalyticsCookies();
    setPromptOpen(false);
  }, []);

  const showBanner = allowBanner && hydrated && (choice === null || promptOpen);

  return (
    <>
      {gtmId && choice === "granted" ? (
        <GoogleTagManager gtmId={gtmId} dataLayer={analyticsContext} />
      ) : null}
      {showBanner ? (
        <CookieConsentBanner
          siteCode={siteCode}
          onAccept={handleAccept}
          onDecline={handleDecline}
        />
      ) : null}
    </>
  );
}

type CookieConsentBannerProps = {
  siteCode: StorefrontSiteCode;
  onAccept: () => void;
  onDecline: () => void;
};

function CookieConsentBanner({
  siteCode,
  onAccept,
  onDecline,
}: CookieConsentBannerProps) {
  return (
    // The per-site theme variables are applied here directly because the
    // banner renders outside StorefrontShell (which normally provides them).
    <div
      role="dialog"
      aria-label="Cookie preferences"
      aria-live="polite"
      data-storefront-site={siteCode}
      style={buildStorefrontThemeVariables(siteCode)}
      className="fixed inset-x-0 bottom-0 z-[70] p-3 sm:p-4"
    >
      <div className="storefront-footer-background mx-auto max-w-3xl rounded-[2rem] border border-white/18 p-5 text-white shadow-2xl backdrop-blur-sm sm:p-6">
        <p className="text-[0.72rem] font-bold uppercase tracking-[0.28em] text-white">
          Cookies
        </p>
        <p className="mt-3 text-sm leading-relaxed text-white/90">
          We use optional analytics cookies to understand how our site is used
          and improve it. Essential storage — your basket and sign-in — always
          works and doesn&apos;t need cookies you can opt out of. See our{" "}
          <Link
            href="/more/privacy-policy"
            className="font-semibold underline underline-offset-2 hover:text-white"
          >
            Privacy Policy
          </Link>{" "}
          for details.
        </p>
        <div className="mt-5 flex flex-wrap gap-2.5">
          <button
            type="button"
            onClick={onAccept}
            className="inline-flex items-center justify-center rounded-full bg-white px-5 py-2.5 text-sm font-semibold storefront-text-primary transition-opacity hover:opacity-90"
          >
            Accept analytics cookies
          </button>
          <button
            type="button"
            onClick={onDecline}
            className="inline-flex items-center justify-center rounded-full border border-white/30 bg-black/10 px-5 py-2.5 text-sm font-medium text-white/90 transition-colors hover:bg-black/20"
          >
            Essential only
          </button>
        </div>
      </div>
    </div>
  );
}
